// The trip to work (Restid) drawn without map tiles: the route's line in each mode's colour, fitted into a
// box. The lines come from /travel/route (`shape`: an encoded polyline, precision 5, simplified by the
// backend); a backend without them sends none, and then there's nothing to draw (`tripModes` is empty).

export const decodePolyline = (text, precision = 5) => {
    const points = [];
    const factor = 10 ** precision;
    let index = 0;
    let lat = 0;
    let lng = 0;
    const next = () => {
        let shift = 0;
        let result = 0;
        let byte;
        do {
            if (index >= text.length) throw new Error('cut short');
            byte = text.charCodeAt(index++) - 63;
            result |= (byte & 0x1f) << shift;
            shift += 5;
        } while (byte >= 0x20);
        return result & 1 ? ~(result >> 1) : result >> 1;
    };
    try {
        while (index < String(text || '').length) {
            lat += next();
            lng += next();
            points.push([lat / factor, lng / factor]);
        }
    } catch (error) {
        return [];
    }
    return points;
};

const RAIL_MODES = new Set(['RAIL', 'HIGHSPEED_RAIL', 'LONG_DISTANCE', 'NIGHT_RAIL', 'REGIONAL_FAST_RAIL', 'REGIONAL_RAIL', 'SUBURBAN']);

// What kind of leg it is, from MOTIS's mode. SL's pendeltåg (lines 40–48) come as regional rail.
export const legKind = (leg) => {
    const mode = String(leg?.mode || '').toUpperCase();
    if (mode === 'WALK') return 'walk';
    if (mode === 'BUS' || mode === 'COACH') return 'bus';
    if (mode === 'SUBWAY' || mode === 'METRO') return 'metro';
    if (mode === 'TRAM') return 'tram';
    if (mode === 'FERRY') return 'ferry';
    if (mode === 'SUBURBAN' || (RAIL_MODES.has(mode) && /^4\d$/.test(String(leg?.line || '')))) return 'commuter';
    return 'train';
};

const KIND_NAMES = {
    walk: { sv: 'Gå', en: 'Walk' },
    bus: { sv: 'Buss', en: 'Bus' },
    metro: { sv: 'T-bana', en: 'Metro' },
    tram: { sv: 'Spårvagn', en: 'Tram' },
    ferry: { sv: 'Båt', en: 'Ferry' },
    commuter: { sv: 'Pendeltåg', en: 'Commuter train' },
    train: { sv: 'Tåg', en: 'Train' },
};

export const legName = (leg, swedish) => KIND_NAMES[legKind(leg)][swedish ? 'sv' : 'en'];

const KIND_TOKENS = {
    walk: 'var(--commute-walk)',
    bus: 'var(--commute-bus)',
    metro: 'var(--commute-metro)',
    tram: 'var(--commute-tram)',
    ferry: 'var(--commute-train)',
    commuter: 'var(--commute-commuter)',
    train: 'var(--commute-train)',
};

// Stockholm's tunnelbana by line, for timetables that leave the colour out.
const METRO_LINE_COLOURS = { 10: '#0a6bb8', 11: '#0a6bb8', 13: '#e2231a', 14: '#e2231a', 17: '#179c46', 18: '#179c46', 19: '#179c46' };

// The line's own colour when the timetable has one, else its kind's.
export const legColour = (leg) => {
    const kind = legKind(leg);
    if (kind === 'walk') return KIND_TOKENS.walk;
    if (leg?.colour) return leg.colour;
    if (kind === 'metro' && METRO_LINE_COLOURS[leg?.line]) return METRO_LINE_COLOURS[leg.line];
    return KIND_TOKENS[kind];
};

export const MODE_LINE_COLOURS = {
    bike: 'var(--trip-bike)',
    car: 'var(--trip-car)',
    walk: 'var(--commute-walk)',
};

// A stop's name as the trip shows it: "Ulriksdal station" → "Ulriksdal", "Farsta T-bana" → "Farsta".
export const stopName = (name) => String(name || '')
    .replace(/\s+(station|T-bana|tågstation|busstation)$/i, '')
    .trim();

// The lines to draw for one mode: [{ kind, colour, points: [[lat, lng], …], leg }].
export const tripSegments = (route, mode) => {
    if (mode === 'transit') {
        return (route?.transit?.legs || []).map((leg) => ({
            kind: legKind(leg) === 'walk' ? 'walk' : 'transit',
            colour: legColour(leg),
            points: decodePolyline(leg.shape || ''),
            leg,
        }));
    }
    const shape = route?.[mode]?.shape;
    return shape ? [{ kind: mode === 'walk' ? 'walk' : mode, colour: MODE_LINE_COLOURS[mode], points: decodePolyline(shape) }] : [];
};

const hasLine = (route, mode) => tripSegments(route, mode).some((segment) => segment.points.length > 1);

// The modes the trip can be drawn for, in the row's order. Walking only when it's the chosen mode or under an hour.
export const tripModes = (route, chosen) => ['transit', 'bike', 'car', 'walk'].filter((mode) => (
    route?.[mode]?.minutes != null
    && (mode !== 'walk' || mode === chosen || route.walk.minutes <= 60)
    && hasLine(route, mode)
));

const MIN_SPAN_M = 700; // a short trip isn't blown up past a few streets
const M_PER_DEG = 111320;

// Fits the lines (and the two ends) into a box of width × height inside `pad`, north up. Returns the
// projection and how many metres one pixel is.
export const fitTrip = (segments, ends, width, height, pad) => {
    const points = [...segments.flatMap((segment) => segment.points), ...ends.filter(Boolean)];
    if (!points.length || width <= 0 || height <= 0) return null;
    const lat0 = points.reduce((sum, [lat]) => sum + lat, 0) / points.length;
    const k = Math.cos((lat0 * Math.PI) / 180);
    let minX = Infinity; let maxX = -Infinity; let minY = Infinity; let maxY = -Infinity;
    points.forEach(([lat, lng]) => {
        const x = lng * k;
        const y = -lat;
        minX = Math.min(minX, x); maxX = Math.max(maxX, x);
        minY = Math.min(minY, y); maxY = Math.max(maxY, y);
    });
    const minSpan = MIN_SPAN_M / M_PER_DEG;
    const spanX = Math.max(maxX - minX, minSpan);
    const spanY = Math.max(maxY - minY, minSpan);
    const availW = Math.max(1, width - pad.left - pad.right);
    const availH = Math.max(1, height - pad.top - pad.bottom);
    const scale = Math.min(availW / spanX, availH / spanY);
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const ox = pad.left + availW / 2;
    const oy = pad.top + availH / 2;
    return {
        project: ([lat, lng]) => [ox + (lng * k - cx) * scale, oy + (-lat - cy) * scale],
        metresPerPx: M_PER_DEG / scale,
    };
};

export const pathData = (pixels) => pixels.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join('');

// The point halfway along a line, in pixels.
export const midpoint = (pixels) => {
    const lengths = pixels.slice(1).map((p, i) => Math.hypot(p[0] - pixels[i][0], p[1] - pixels[i][1]));
    let rest = lengths.reduce((sum, d) => sum + d, 0) / 2;
    for (let i = 0; i < lengths.length; i += 1) {
        if (rest <= lengths[i] && lengths[i] > 0) {
            const t = rest / lengths[i];
            return [pixels[i][0] + (pixels[i + 1][0] - pixels[i][0]) * t, pixels[i][1] + (pixels[i + 1][1] - pixels[i][1]) * t];
        }
        rest -= lengths[i];
    }
    return pixels[0];
};

const NICE_METRES = [50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000];

// A scale bar about `targetPx` long: { px, label }.
export const scaleBar = (metresPerPx, targetPx = 90) => {
    const want = metresPerPx * targetPx;
    const metres = NICE_METRES.filter((m) => m <= want).pop() || NICE_METRES[0];
    return { px: metres / metresPerPx, label: metres >= 1000 ? `${metres / 1000} km` : `${metres} m` };
};

let measureContext = null;
// A label's width in pixels in the given CSS font (canvas measureText; a rough guess without a canvas).
export const textWidth = (text, font) => {
    if (!measureContext && typeof document !== 'undefined') {
        measureContext = document.createElement('canvas').getContext('2d');
    }
    if (!measureContext) return String(text).length * 7;
    measureContext.font = font;
    return measureContext.measureText(String(text)).width;
};

const overlaps = (a, b) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

// Places boxes of w × h next to a point, trying the sides in turn, and keeps the first spot that stays
// inside `bounds` and clear of what's already placed (`taken`, which it adds to). Returns the box or null.
export const placeBox = (point, w, h, taken, bounds, gap = 9) => {
    const [x, y] = point;
    const spots = [
        { x: x + gap, y: y - h / 2 },
        { x: x - gap - w, y: y - h / 2 },
        { x: x - w / 2, y: y - gap - h },
        { x: x - w / 2, y: y + gap },
        { x: x + gap, y: y - h - gap / 2 },
        { x: x - gap - w, y: y + gap / 2 },
    ];
    const spot = spots
        .map((s) => ({ ...s, w, h }))
        .find((box) => box.x >= bounds.x && box.y >= bounds.y && box.x + w <= bounds.x + bounds.w && box.y + h <= bounds.y + bounds.h
            && !taken.some((other) => overlaps(box, other)));
    if (spot) taken.push(spot);
    return spot || null;
};
